import type {
	Monitor as MonitorState,
	ScreenerState,
} from "epsm-api-types/models";
import { useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { DEFAULT_STATE as DEFAULT_MONITOR_STATE } from "@/components/widgets/Monitor/defaultState";
import { filterWidgetState } from "@/components/widgets/Monitor/lib/helpers/filterWidgetState";
import type { MonitorStateService } from "@/components/widgets/Monitor/lib/services/MonitorStateService";
import { DEFAULT_STATE as DEFAULT_SCREENER_STATE } from "@/components/widgets/Screener/default";
import type { ScreenerStateService } from "@/components/widgets/Screener/services/ScreenerStateService";
import { defaultScreenerAndMonitorState } from "@/context/tabs/defaultTabValues";
import { useGetUserDataFromToken } from "@/hooks/useGetUserDataFromToken";
import { EVENTS } from "@/shared/eventEmiters/consts";
import type { EventEmitter } from "@/shared/eventEmiters/EventEmitter";
import { getDefaultName } from "@/shared/helpers/getDefaultName";
import type { ViewId } from "@/shared/types/common";

type StateType = ScreenerState | MonitorState;

function isScreener(state: StateType): state is ScreenerState {
	return "filter" in state;
}

export const useScreenerState = (
	eventEmitter: EventEmitter,
	widgetName: ViewId,
	widgetId: string,
	service: ScreenerStateService | MonitorStateService,
	widgetState?: Record<string, unknown>,
) => {
	const userData = useGetUserDataFromToken();
	const userId = userData?.username || "";

	const defaultState = useMemo<StateType>(
		() =>
			widgetName === "Screener"
				? DEFAULT_SCREENER_STATE
				: DEFAULT_MONITOR_STATE,
		[widgetName],
	);

	const [states, setStates] = useState<StateType[]>([]);
	const [state, setState] = useState<StateType>({
		...defaultState,
		...filterWidgetState(defaultState, widgetState || {}),
	});
	const [name, setName] = useState<string>(state.itemName || "");

	const isFirstRender = useRef(true);

	const { toast } = useToast(widgetId);

	// biome-ignore lint/correctness/useExhaustiveDependencies: this effect should only run when widgetState changes, as it handles initial state initialization
	useEffect(() => {
		if (
			JSON.stringify(widgetState) !==
				JSON.stringify(defaultScreenerAndMonitorState) &&
			isFirstRender.current === true
		) {
			isFirstRender.current = false;
			const stateWithMatchingFields = filterWidgetState(
				defaultState,
				widgetState ?? {},
			);
			const newState =
				widgetName === "Screener"
					? ({ ...stateWithMatchingFields, userId: userId } as ScreenerState)
					: { ...state, ...stateWithMatchingFields };
			getNewState(newState);
		}
	}, [widgetState]);

	const getNewState = (state: StateType) => {
		setState(state);
		setName(state.itemName);
		eventEmitter.emit(EVENTS.WIDGET_STATE, state);
	};

	const getStates = () => {
		if (!userId) return;

		service
			.getStates(userId)
			.then((response) => setStates(response.data ?? []));
	};

	const getState = (name: string) => {
		if (!userId) return;

		service
			.getState(userId, name)
			.then((response) => response.data && getNewState(response.data));
	};

	const removeState = async (
		event: React.MouseEvent<HTMLDivElement>,
		name: string,
	) => {
		if (!userId) return;

		event.stopPropagation();
		service.removeState(userId, name).then(() => {
			setStates((prevStates) =>
				prevStates.filter((state) => state.itemName !== name),
			);
		});

		if (state.itemName === name) {
			const newName = await getDefaultName(
				widgetName,
				userId,
				setName,
				service,
			);
			getNewState({ ...defaultState, itemName: newName });
		}
	};

	const addState = () => {
		if (!userId) return;

		const newState: ScreenerState | MonitorState = {
			userId,
			itemName: name.trim(),
			...(state?.fields?.length && {
				fields: state.fields,
			}),
			...(!isScreener(state) &&
				state?.tickers?.length && {
					tickers: state?.tickers,
				}),
			...(state?.sort?.length && {
				sort: state.sort,
			}),
			...(isScreener(state) &&
				state?.filter?.length && {
					filter: state.filter,
				}),
		};

		const nameMatchIndex = states.findIndex((state) => state.itemName === name);

		service.addState(userId, name, newState).then(() => {
			if (nameMatchIndex >= 0) {
				const updatedStates = [...states];
				updatedStates[nameMatchIndex] = {
					...newState,
				};

				setStates(updatedStates);
				toast({ title: `${name} - Updated` });
			} else {
				setStates((prevStates) => [...prevStates, newState]);
				toast({ title: `${name} - Saved` });
			}
		});
	};

	const resetState = async () => {
		if (!userId) return;

		const newName = await getDefaultName(widgetName, userId, setName, service);
		getNewState({ ...defaultState, itemName: newName || "" });
		toast({ title: `New ${newName} created` });
	};

	return {
		states,
		state,
		name,
		setName,
		setState,
		getStates,
		getState,
		removeState,
		addState,
		resetState,
	};
};
